import React from "react";
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";

import {
    FiArrowRight,
    FiCheckCircle,
    FiChevronRight,
    FiCode,
    FiCpu,
    FiDatabase,
    FiExternalLink,
    FiGitBranch,
    FiLayers,
    FiMessageSquare,
    FiPlayCircle,
    FiServer,
    FiShield,
    FiTarget,
    FiTool,
    FiTrendingUp,
    FiZap,
} from "react-icons/fi";

import "./RAGProjectPage.css";
import GetInTouch from "../../components/common/Contact/GetInTouch";
import SEO from "../../components/common/seo/SEO";

function RAGProjectPage() {
    const [openFaq, setOpenFaq] = React.useState("1");

    const toggleFaq = (id) => {
        setOpenFaq(openFaq === id ? "" : id);
    };

    const lessonPlan = [
        {
            number: "01",
            title: "Neural Networks Essentials",
            duration: "2 Hours",
            icon: <FiCpu />,
            topics: [
                "Artificial neurons, perceptrons, and feedforward networks",
                "Quick introduction to backpropagation and activation functions",
                "Overview of CNNs and RNNs for transformer context",
            ],
        },
        {
            number: "02",
            title: "NLP Evolution & Transformers",
            duration: "2 Hours",
            icon: <FiMessageSquare />,
            topics: [
                "From rule-based NLP to deep learning NLP",
                "Why Transformers replaced RNNs and LSTMs",
                "Self-attention and positional encoding",
            ],
        },
        {
            number: "03",
            title: "Embeddings & Vector Databases",
            duration: "2 Hours",
            icon: <FiDatabase />,
            topics: [
                "Word embeddings including Word2Vec, BERT and Sentence Transformers",
                "Generate embeddings using Hugging Face",
                "Introduction to FAISS, Chroma and Pinecone",
                "Understand when and why to use vector databases",
            ],
        },
        {
            number: "04",
            title: "Retrieval-Augmented Generation (RAG)",
            duration: "1.5 Hours",
            icon: <FiLayers />,
            topics: [
                "Why RAG and the limitations of standalone LLMs",
                "RAG architecture: Retriever + Generator",
                "Common use cases including chatbots, Q&A and knowledge grounding",
            ],
        },
        {
            number: "05",
            title: "RAG Practical Implementation",
            duration: "3–4 Hours",
            icon: <FiCode />,
            topics: [
                "Set up PostgreSQL or FAISS/Chroma for storage",
                "Store and retrieve embeddings",
                "Use LangChain to connect retrievers with OpenAI or Hugging Face models",
                "Implement Query → Retrieve → Generate workflow",
                "Basic error handling and performance checks",
            ],
        },
        {
            number: "06",
            title: "LLM Fine-Tuning vs RAG",
            duration: "1 Hour",
            icon: <FiGitBranch />,
            topics: [
                "Understand fine-tuning",
                "Cost, flexibility and scalability comparison",
                "Understand when to combine fine-tuning and RAG",
            ],
        },
        {
            number: "07",
            title: "Capstone Project",
            duration: "2–2.5 Hours",
            icon: <FiTarget />,
            topics: [
                "Build a minimal RAG-powered Q&A system",
                "Use a real dataset or documentation such as a company knowledge base",
                "Test and improve the system",
                "Present the final project and results",
            ],
        },
    ];

    const faqs = [
        {
            id: "1",
            question: "I've wasted money on courses before. How is this different?",
            answer:
                "This is not just theory. You will build a fully working RAG-based AI project step by step with practical guidance. The focus is on implementation, project development and portfolio-ready skills.",
        },
        {
            id: "2",
            question: "I'm not confident in coding yet. Can I still join?",
            answer:
                "If you have completed some basic programming, especially Python programming, you can join. If you are completely new to programming, it is recommended to complete a foundation course before starting this project.",
        },
        {
            id: "3",
            question: "Is this project useful for job interviews or placements?",
            answer:
                "Yes. You will complete a practical RAG project that can be demonstrated during interviews and added to your GitHub portfolio and resume.",
        },
        {
            id: "4",
            question: "Is this project beginner-friendly?",
            answer:
                "The project is suitable for learners who have basic to intermediate programming knowledge and some familiarity with machine learning, neural networks or NLP concepts.",
        },
        {
            id: "5",
            question: "What tools do I need before starting?",
            answer:
                "You should have Python and VS Code installed before starting. Depending on the implementation, additional tools and libraries such as Hugging Face, LangChain, vector databases and FastAPI will be introduced during the project.",
        },
        {
            id: "6",
            question: "Is this really worth my time?",
            answer:
                "The project is designed around practical RAG implementation, including embeddings, vector search, retrieval pipelines and LLM integration. The lesson plan combines foundational concepts with hands-on implementation.",
        },
        {
            id: "7",
            question: "Will I receive a certificate after the course?",
            answer:
                "Yes, you will receive an industry-recognized certificate after completing the project training.",
        },
        {
            id: "8",
            question: "Are you looking to hire candidates for companies?",
            answer:
                "Yes. FEESWORTH also provides HR and recruitment services to help businesses find suitable candidates. You can contact the team to discuss your hiring requirements.",
        },
    ];

    const relatedProjects = [
        {
            title: "IT Support Agentic AI",
            description:
                "Build an AI-powered IT support automation system that processes email-based IT requests, identifies intents such as GitHub access or revoke requests, creates tickets and performs GitHub actions.",
            link: "/replace-it-help-desk-with-automated-ticketing-system-using-ai",
            icon: <FiServer />,
        },
        {
            title: "Intelligent Manufacturing Quality Control System For Tire Production",
            description:
                "Develop an AI-powered quality control system for tyre manufacturing that analyzes production-line videos, detects deviations and uses a GenAI pipeline for root cause analysis.",
            link: "https://feesworth.com/ai-ml-project-intelligent-manufacturing-quality-control-system-for-tire-production/",
            icon: <FiTrendingUp />,
        },
        {
            title: "Image Classification Using Naive & Deep Learning Techniques",
            description:
                "Develop an image classification system using a classical computer vision pipeline with SVM and Histogram of Oriented Gradients (HOG) feature extraction.",
            link: "/ai-ml-project-image-classification-deep-learning",
            icon: <FiZap />,
        },
        {
            title: "Brain Stroke Prediction using OpenCV and CNN",
            description:
                "Develop an AI-based brain stroke prediction system using OpenCV and CNNs to analyze MRI or CT scans with TensorFlow and Keras.",
            link: "/ai-ml-project-brain-stroke-prediction-opencv-cnn",
            icon: <FiShield />,
        },
    ];

    const objectives = [
        "Understand the core concepts of Retrieval-Augmented Generation (RAG).",
        "Integrate external knowledge sources with large language models to improve output accuracy.",
        "Implement semantic search using vector databases such as FAISS and Weaviate.",
        "Design and deploy a complete RAG pipeline using Python and relevant libraries.",
        "Evaluate and optimize factual relevance and contextual grounding of LLM responses.",
        "Apply neural network and transformer principles in retrieval-based language generation tasks.",
    ];

    return (
        <>

            <SEO
                title="RAG Project | Retrieval-Augmented Generation AI Project | FeesWorth"
                description="Build a practical Retrieval-Augmented Generation (RAG) project using Python, LLMs, embeddings, vector databases, LangChain, semantic search, and external knowledge sources."
                keywords="RAG Project, Retrieval-Augmented Generation Project, RAG AI Project, RAG Training, LLM RAG Project, LangChain RAG, Vector Database, Semantic Search, AI Project, Generative AI Project, Python RAG Project"
                canonical="https://feesworth.com/ai-ml-project-rag-retrieval-argument-generation"
            />

            <div className="rag-project-page">

                {/* =====================================================
                HERO
            ===================================================== */}

                <section className="rag-hero">

                    <Container>

                        <Row className="align-items-center">

                            <Col lg="7">

                                <div className="rag-hero-content">

                                    <span className="rag-eyebrow">
                                        AI / ML PROJECT
                                    </span>

                                    <h1>
                                        Retrieval-Augmented
                                        <span> Generation (RAG)</span>
                                    </h1>

                                    <p className="rag-hero-description">
                                        Build an intelligent RAG pipeline that
                                        connects large language models with
                                        authoritative external knowledge to
                                        improve factual accuracy, relevance and
                                        contextual grounding.
                                    </p>

                                    <div className="rag-hero-actions">

                                        <Button
                                            color="primary"
                                            className="rag-primary-btn"
                                            onClick={() =>
                                                document
                                                    .getElementById("rag-contact")
                                                    ?.scrollIntoView({
                                                        behavior: "smooth",
                                                    })
                                            }
                                        >
                                            Let Us Call You
                                            <FiArrowRight />
                                        </Button>

                                        <Button
                                            outline
                                            color="light"
                                            className="rag-outline-btn"
                                            onClick={() =>
                                                document
                                                    .getElementById("rag-lessons")
                                                    ?.scrollIntoView({
                                                        behavior: "smooth",
                                                    })
                                            }
                                        >
                                            View Lesson Plan
                                            <FiPlayCircle />
                                        </Button>

                                    </div>

                                    <div className="rag-hero-meta">

                                        <div>
                                            <FiCode />
                                            <span>Python</span>
                                        </div>

                                        <div>
                                            <FiLayers />
                                            <span>LLM + RAG</span>
                                        </div>

                                        <div>
                                            <FiDatabase />
                                            <span>Vector Search</span>
                                        </div>

                                    </div>

                                </div>

                            </Col>


                            <Col lg="5">

                                <div className="rag-hero-visual">

                                    <div className="rag-glow rag-glow-one" />
                                    <div className="rag-glow rag-glow-two" />

                                    <div className="rag-architecture-card">

                                        <div className="rag-architecture-header">
                                            <span>
                                                RAG PIPELINE
                                            </span>

                                            <FiZap />
                                        </div>


                                        <div className="rag-flow">

                                            <div className="rag-flow-item">
                                                <div className="rag-flow-icon">
                                                    <FiMessageSquare />
                                                </div>

                                                <span>User Query</span>
                                            </div>


                                            <div className="rag-flow-line" />


                                            <div className="rag-flow-item">
                                                <div className="rag-flow-icon">
                                                    <FiDatabase />
                                                </div>

                                                <span>Retriever</span>
                                            </div>


                                            <div className="rag-flow-line" />


                                            <div className="rag-flow-item">
                                                <div className="rag-flow-icon">
                                                    <FiLayers />
                                                </div>

                                                <span>Knowledge</span>
                                            </div>


                                            <div className="rag-flow-line" />


                                            <div className="rag-flow-item">
                                                <div className="rag-flow-icon">
                                                    <FiCpu />
                                                </div>

                                                <span>LLM</span>
                                            </div>

                                        </div>


                                        <div className="rag-output-box">

                                            <FiCheckCircle />

                                            <div>
                                                <strong>
                                                    Grounded Response
                                                </strong>

                                                <span>
                                                    Relevant + Contextual + Accurate
                                                </span>
                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                PROJECT DESCRIPTION
            ===================================================== */}

                <section className="rag-overview-section">

                    <Container>

                        <Row className="align-items-center">

                            <Col lg="6">

                                <div className="rag-section-label">
                                    PROJECT OVERVIEW
                                </div>

                                <h2 className="rag-section-title">
                                    What is this project all about?
                                </h2>

                                <p className="rag-section-description">
                                    This project optimizes large language model
                                    outputs by integrating authoritative external
                                    knowledge bases, enhancing accuracy and
                                    relevance beyond pre-trained data sources.
                                </p>

                                <div className="rag-scope-card">

                                    <div className="rag-scope-icon">
                                        <FiTarget />
                                    </div>

                                    <div>

                                        <h4>
                                            Project Scope
                                        </h4>

                                        <p>
                                            The project enhances LLM outputs by
                                            integrating real-time retrieval from
                                            trusted external sources. It focuses
                                            on improving factual accuracy and
                                            contextual relevance while reducing
                                            hallucination.
                                        </p>

                                    </div>

                                </div>

                            </Col>


                            <Col lg="6">

                                <div className="rag-overview-stats">

                                    <div className="rag-stat-card">

                                        <FiDatabase />

                                        <strong>
                                            External
                                        </strong>

                                        <span>
                                            Knowledge Sources
                                        </span>

                                    </div>


                                    <div className="rag-stat-card">

                                        <FiCpu />

                                        <strong>
                                            LLM
                                        </strong>

                                        <span>
                                            Powered Generation
                                        </span>

                                    </div>


                                    <div className="rag-stat-card">

                                        <FiZap />

                                        <strong>
                                            Semantic
                                        </strong>

                                        <span>
                                            Search
                                        </span>

                                    </div>


                                    <div className="rag-stat-card">

                                        <FiTarget />

                                        <strong>
                                            Grounded
                                        </strong>

                                        <span>
                                            Responses
                                        </span>

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                PREREQUISITES
            ===================================================== */}

                <section className="rag-prerequisites-section">

                    <Container>

                        <Row>

                            <Col lg="5">

                                <div className="rag-section-label">
                                    BEFORE YOU START
                                </div>

                                <h2 className="rag-section-title">
                                    What You Should Know Before Starting
                                </h2>

                                <p className="rag-section-description">
                                    A basic technical foundation will help you
                                    get the most out of this hands-on AI/ML
                                    project.
                                </p>

                            </Col>


                            <Col lg="7">

                                <div className="rag-prerequisite-list">

                                    <div className="rag-prerequisite-item">

                                        <div className="rag-check-icon">
                                            <FiCheckCircle />
                                        </div>

                                        <div>
                                            <h4>
                                                Python Programming
                                            </h4>

                                            <p>
                                                A solid understanding of Python
                                                programming is required.
                                            </p>
                                        </div>

                                    </div>


                                    <div className="rag-prerequisite-item">

                                        <div className="rag-check-icon">
                                            <FiCheckCircle />
                                        </div>

                                        <div>
                                            <h4>
                                                Neural Networks
                                            </h4>

                                            <p>
                                                Familiarity with neural networks
                                                and transformer architecture is
                                                expected.
                                            </p>
                                        </div>

                                    </div>


                                    <div className="rag-prerequisite-item">

                                        <div className="rag-check-icon">
                                            <FiCheckCircle />
                                        </div>

                                        <div>
                                            <h4>
                                                Machine Learning
                                            </h4>

                                            <p>
                                                Basic knowledge of machine
                                                learning is a plus but not
                                                mandatory.
                                            </p>
                                        </div>

                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                TOOLS & TECHNOLOGIES
            ===================================================== */}

                <section className="rag-tech-section">

                    <Container>

                        <div className="rag-centered-heading">

                            <div className="rag-section-label">
                                TECH STACK
                            </div>

                            <h2 className="rag-section-title">
                                Tools & Technologies
                            </h2>

                            <p>
                                Work with modern AI, NLP, vector search and
                                deployment technologies used to build practical
                                RAG applications.
                            </p>

                        </div>


                        <Row className="g-4">

                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiCode />
                                        </div>

                                        <h4>
                                            Programming Language
                                        </h4>

                                        <div className="rag-tech-tag">
                                            Python
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>


                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiCpu />
                                        </div>

                                        <h4>
                                            AI & Deep Learning
                                        </h4>

                                        <div className="rag-tech-tags">
                                            <span>Hugging Face Transformers</span>
                                            <span>PyTorch</span>
                                            <span>TensorFlow</span>
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>


                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiLayers />
                                        </div>

                                        <h4>
                                            RAG Orchestration
                                        </h4>

                                        <div className="rag-tech-tags">
                                            <span>LangChain</span>
                                            <span>LlamaIndex</span>
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>


                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiDatabase />
                                        </div>

                                        <h4>
                                            Vector Search
                                        </h4>

                                        <div className="rag-tech-tags">
                                            <span>FAISS</span>
                                            <span>Weaviate</span>
                                            <span>Milvus</span>
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>


                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiServer />
                                        </div>

                                        <h4>
                                            Data Retrieval
                                        </h4>

                                        <div className="rag-tech-tags">
                                            <span>ElasticSearch</span>
                                            <span>Pinecone</span>
                                            <span>Web Search APIs</span>
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>


                            <Col md="6" lg="4">

                                <Card className="rag-tech-card">
                                    <CardBody>

                                        <div className="rag-tech-icon">
                                            <FiTool />
                                        </div>

                                        <h4>
                                            Hosting & Deployment
                                        </h4>

                                        <div className="rag-tech-tags">
                                            <span>FastAPI</span>
                                            <span>Docker</span>
                                            <span>AWS</span>
                                            <span>GCP</span>
                                            <span>Azure</span>
                                        </div>

                                    </CardBody>
                                </Card>

                            </Col>

                        </Row>


                        <div className="rag-additional-tools">

                            <strong>
                                Version Control & Collaboration:
                            </strong>

                            <span>Git</span>
                            <span>GitHub</span>

                            <strong>
                                Optional:
                            </strong>

                            <span>OpenAI API</span>

                        </div>

                    </Container>

                </section>


                {/* =====================================================
                LEARNING OBJECTIVES
            ===================================================== */}

                <section className="rag-objectives-section">

                    <Container>

                        <Row className="align-items-center">

                            <Col lg="5">

                                <div className="rag-objectives-visual">

                                    <div className="rag-objectives-main-icon">
                                        <FiTarget />
                                    </div>

                                    <div className="rag-objective-floating floating-one">
                                        <FiCpu />
                                        <span>LLMs</span>
                                    </div>

                                    <div className="rag-objective-floating floating-two">
                                        <FiDatabase />
                                        <span>Vector DB</span>
                                    </div>

                                    <div className="rag-objective-floating floating-three">
                                        <FiZap />
                                        <span>Retrieval</span>
                                    </div>

                                </div>

                            </Col>


                            <Col lg="7">

                                <div className="rag-section-label">
                                    LEARNING OBJECTIVES
                                </div>

                                <h2 className="rag-section-title">
                                    What You Will Learn
                                </h2>

                                <p className="rag-section-description">
                                    Develop the technical understanding and
                                    practical skills required to design and
                                    implement a complete RAG solution.
                                </p>


                                <div className="rag-objective-list">

                                    {objectives.map((objective, index) => (

                                        <div
                                            className="rag-objective-item"
                                            key={index}
                                        >

                                            <FiCheckCircle />

                                            <span>
                                                {objective}
                                            </span>

                                        </div>

                                    ))}

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                LESSON PLAN
            ===================================================== */}

                <section
                    className="rag-lessons-section"
                    id="rag-lessons"
                >

                    <Container>

                        <div className="rag-centered-heading">

                            <div className="rag-section-label">
                                LESSON PLAN
                            </div>

                            <h2 className="rag-section-title">
                                Build Your RAG Project Step by Step
                            </h2>

                            <p>
                                The project moves from foundational AI concepts
                                to practical RAG implementation and a final
                                working capstone.
                            </p>

                        </div>


                        <div className="rag-timeline">

                            {lessonPlan.map((lesson) => (

                                <div
                                    className="rag-lesson"
                                    key={lesson.number}
                                >

                                    <div className="rag-lesson-number">
                                        {lesson.number}
                                    </div>

                                    <div className="rag-lesson-line" />

                                    <Card className="rag-lesson-card">

                                        <CardBody>

                                            <div className="rag-lesson-header">

                                                <div className="rag-lesson-title-wrap">

                                                    <div className="rag-lesson-icon">
                                                        {lesson.icon}
                                                    </div>

                                                    <div>

                                                        <h3>
                                                            {lesson.title}
                                                        </h3>

                                                        <span>
                                                            {lesson.duration}
                                                        </span>

                                                    </div>

                                                </div>

                                                <FiChevronRight />

                                            </div>


                                            <div className="rag-lesson-topics">

                                                {lesson.topics.map(
                                                    (topic, topicIndex) => (

                                                        <div
                                                            key={topicIndex}
                                                        >

                                                            <FiCheckCircle />

                                                            <span>
                                                                {topic}
                                                            </span>

                                                        </div>

                                                    )
                                                )}

                                            </div>

                                        </CardBody>

                                    </Card>

                                </div>

                            ))}

                        </div>

                    </Container>

                </section>


                {/* =====================================================
                FAQ
            ===================================================== */}

                <section className="rag-faq-section">

                    <Container>

                        <Row className="justify-content-center">

                            <Col lg="9">

                                <div className="rag-centered-heading">

                                    <div className="rag-section-label">
                                        FAQ
                                    </div>

                                    <h2 className="rag-section-title">
                                        Frequently Asked Questions
                                    </h2>

                                    <p>
                                        Everything you need to know before
                                        starting the RAG project.
                                    </p>

                                </div>


                                <Accordion
                                    open={openFaq}
                                    toggle={toggleFaq}
                                    className="rag-faq"
                                >

                                    {faqs.map((faq) => (

                                        <AccordionItem
                                            key={faq.id}
                                        >

                                            <AccordionHeader
                                                targetId={faq.id}
                                            >
                                                {faq.question}
                                            </AccordionHeader>

                                            <AccordionBody
                                                accordionId={faq.id}
                                            >
                                                {faq.answer}
                                            </AccordionBody>

                                        </AccordionItem>

                                    ))}

                                </Accordion>

                            </Col>

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                RELATED PROJECTS
            ===================================================== */}

                <section className="rag-related-section">

                    <Container>

                        <div className="rag-centered-heading">

                            <div className="rag-section-label">
                                EXPLORE MORE
                            </div>

                            <h2 className="rag-section-title">
                                More AI & ML Projects
                            </h2>

                            <p>
                                Explore other practical AI and machine learning
                                projects available through FEESWORTH.
                            </p>

                        </div>


                        <Row className="g-4">

                            {relatedProjects.map(
                                (project, index) => (

                                    <Col
                                        md="6"
                                        lg="3"
                                        key={index}
                                    >

                                        <Card className="rag-related-card">

                                            <CardBody>

                                                <div className="rag-related-icon">
                                                    {project.icon}
                                                </div>

                                                <h3>
                                                    {project.title}
                                                </h3>

                                                <p>
                                                    {project.description}
                                                </p>

                                                <a
                                                    href={project.link}
                                                    className="rag-related-link"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    View Project
                                                    <FiExternalLink />
                                                </a>

                                            </CardBody>

                                        </Card>

                                    </Col>

                                )
                            )}

                        </Row>

                    </Container>

                </section>


                {/* =====================================================
                CONTACT CTA
            ===================================================== */}

                {/* =====================================================
    CONTACT / GET IN TOUCH
===================================================== */}

                <section
                    className="rag-contact-section"
                    id="rag-contact"
                >
                    <Container>

                        <div className="rag-contact-heading">
                            <span>
                                READY TO BUILD?
                            </span>

                            <h2>
                                Let's Discuss Your RAG Project
                            </h2>

                            <p>
                                Have questions about the project, tools, prerequisites,
                                or training? Send us your details and our team will
                                get back to you.
                            </p>
                        </div>

                        <div className="rag-contact-wrapper">

                            <GetInTouch
                                courseName="RAG (Retrieval-Augmented Generation) AI/ML Project"
                                title="Get in Touch"
                                subtitle="Interested in building this RAG project? Send us your details and our team will contact you."
                            />

                        </div>

                    </Container>
                </section>
            </div>
        </>

    );
}

export default RAGProjectPage;